import React, { useState, useEffect } from "react";
import axios from "axios";
import AdminLayout from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Mail, Save, Send, Eye, EyeOff, Info } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailSettings = () => {
  const [settings, setSettings] = useState({
    smtp_email: "",
    smtp_password: "",
    admin_notification_email: "",
    email_enabled: false,
    guest_subject: "Bókunarstaðfesting - {property_name}",
    guest_body: `Kæri/kæra {guest_name},

Takk fyrir bókunina þína!

Bókunarupplýsingar:
- Innritun: {check_in}
- Útritun: {check_out}
- Fjöldi gesta: {num_guests}
- Heildarverð: €{total_price}

Við hlökkum til að taka á móti þér!

Kveðja,
{property_name}`,
    admin_subject: "Ný bókun móttekin - {guest_name}",
    admin_body: `Ný bókun hefur borist!

Gestur: {guest_name}
Tölvupóstur: {guest_email}
Sími: {guest_phone}

Dagsetningar: {check_in} til {check_out}
Fjöldi gesta: {num_guests}
Heildarverð: €{total_price}

Séróskir: {special_requests}`,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/email-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Error fetching email settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/email-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Stillingar vistaðar!");
    } catch (error) {
      toast.error("Villa við að vista stillingar");
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!settings.smtp_email || !settings.smtp_password || settings.smtp_password === "••••••••••••••••") {
      toast.error("Vistaðu SMTP stillingar fyrst (tölvupóst og App Password)");
      return;
    }
    // Auto-save before testing
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      await axios.put(`${API}/email-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      toast.error("Villa við að vista stillingar");
      setSaving(false);
      return;
    }
    setSaving(false);

    setTesting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/email-settings/test`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`${response.data.message} (${response.data.sent_to})`);
    } catch (error) {
      const detail = error.response?.data?.detail || "Villa við að senda prófunarpóst";
      toast.error(detail);
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-4xl">
        <div className="flex items-center gap-3 mb-8">
          <Mail className="w-8 h-8 text-primary" />
          <h1 className="text-3xl md:text-4xl font-display font-bold tracking-tight" data-testid="email-settings-title">
            Tölvupóststillingar
          </h1>
        </div>

        {/* Enable/Disable Toggle */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-heading font-semibold">Virkja tölvupósttilkynningar</h2>
              <p className="text-sm text-muted-foreground mt-1">
                Þegar virkt, sendir kerfið sjálfvirkan tölvupóst til gesta og admin við nýja bókun
              </p>
            </div>
            <Switch
              checked={settings.email_enabled}
              onCheckedChange={(checked) => setSettings({ ...settings, email_enabled: checked })}
              data-testid="email-enabled-switch"
            />
          </div>
        </div>

        {/* SMTP Settings */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-heading font-semibold mb-4">Google Workspace / Gmail Stillingar</h2>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <div className="flex gap-2">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Hvernig á að búa til App Password:</p>
                <ol className="list-decimal list-inside space-y-1">
                  <li>Farðu á <a href="https://myaccount.google.com/security" target="_blank" rel="noopener noreferrer" className="underline">Google Account Security</a></li>
                  <li>Kveiktu á "2-Step Verification" ef ekki þegar virkt</li>
                  <li>Farðu í "App passwords" (undir 2-Step Verification)</li>
                  <li>Búðu til nýtt app password fyrir "Mail"</li>
                  <li>Afritaðu 16 stafa lykilorðið hér að neðan</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label htmlFor="smtp_email" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Tölvupóstfang (Gmail/Google Workspace)
              </Label>
              <Input
                id="smtp_email"
                type="email"
                value={settings.smtp_email || ""}
                onChange={(e) => setSettings({ ...settings, smtp_email: e.target.value })}
                placeholder="booking@yourdomain.com"
                className="px-4 py-2 rounded-lg"
                data-testid="smtp-email-input"
              />
            </div>

            <div>
              <Label htmlFor="smtp_password" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                App Password (16 stafir)
              </Label>
              <div className="relative">
                <Input
                  id="smtp_password"
                  type={showPassword ? "text" : "password"}
                  value={settings.smtp_password || ""}
                  onChange={(e) => setSettings({ ...settings, smtp_password: e.target.value })}
                  placeholder="xxxx xxxx xxxx xxxx"
                  className="px-4 py-2 rounded-lg pr-10"
                  data-testid="smtp-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="md:col-span-2">
              <Label htmlFor="admin_email" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Admin tilkynningapóstur (þangað sem þú færð tilkynningar)
              </Label>
              <Input
                id="admin_email"
                type="email"
                value={settings.admin_notification_email || ""}
                onChange={(e) => setSettings({ ...settings, admin_notification_email: e.target.value })}
                placeholder="admin@yourdomain.com"
                className="px-4 py-2 rounded-lg"
                data-testid="admin-email-input"
              />
            </div>
          </div>
        </div>

        {/* Guest Email Template */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-heading font-semibold mb-4">Tölvupóstur til gesta</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Breytur: {"{guest_name}"}, {"{check_in}"}, {"{check_out}"}, {"{num_guests}"}, {"{total_price}"}, {"{property_name}"}
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="guest_subject" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Efnislína
              </Label>
              <Input
                id="guest_subject"
                value={settings.guest_subject || ""}
                onChange={(e) => setSettings({ ...settings, guest_subject: e.target.value })}
                className="px-4 py-2 rounded-lg"
                data-testid="guest-subject-input"
              />
            </div>

            <div>
              <Label htmlFor="guest_body" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Meginmál
              </Label>
              <Textarea
                id="guest_body"
                value={settings.guest_body || ""}
                onChange={(e) => setSettings({ ...settings, guest_body: e.target.value })}
                rows={10}
                className="px-4 py-2 rounded-lg font-mono text-sm"
                data-testid="guest-body-input"
              />
            </div>
          </div>
        </div>

        {/* Admin Email Template */}
        <div className="bg-white border border-neutral-200 rounded-lg p-6 mb-6">
          <h2 className="text-xl font-heading font-semibold mb-4">Tilkynning til admin</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Breytur: {"{guest_name}"}, {"{guest_email}"}, {"{guest_phone}"}, {"{check_in}"}, {"{check_out}"}, {"{num_guests}"}, {"{total_price}"}, {"{special_requests}"}, {"{property_name}"}
          </p>

          <div className="space-y-4">
            <div>
              <Label htmlFor="admin_subject" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Efnislína
              </Label>
              <Input
                id="admin_subject"
                value={settings.admin_subject || ""}
                onChange={(e) => setSettings({ ...settings, admin_subject: e.target.value })}
                className="px-4 py-2 rounded-lg"
                data-testid="admin-subject-input"
              />
            </div>

            <div>
              <Label htmlFor="admin_body" className="text-sm font-ui font-medium text-neutral-700 mb-2 block">
                Meginmál
              </Label>
              <Textarea
                id="admin_body"
                value={settings.admin_body || ""}
                onChange={(e) => setSettings({ ...settings, admin_body: e.target.value })}
                rows={10}
                className="px-4 py-2 rounded-lg font-mono text-sm"
                data-testid="admin-body-input"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap gap-4">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-primary hover:bg-primary-600"
            data-testid="save-settings-button"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Vista..." : "Vista stillingar"}
          </Button>

          <Button
            onClick={handleTest}
            disabled={testing || !settings.smtp_email || !settings.smtp_password}
            variant="outline"
            data-testid="test-email-button"
          >
            <Send className="w-4 h-4 mr-2" />
            {testing ? "Sendi..." : "Senda prófunarpóst"}
          </Button>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EmailSettings;
